// homePrefsBootSource.js — the one decision behind "whose answer does the home
// board boot from: the SERVER row, or the local cache?"
//
// THE RULE, and it has one direction only: the per-user × per-workspace server
// row (`home_dashboard_prefs`) is the single source of truth for a widget
// hide/show and for the grid layout. `localStorage` is a BOOT CACHE — it exists
// so the board can paint before the prefs GET lands, and for a boot with no
// network. It may never outrank the server.
//
// THE BUG THIS PINS, measured live on vault.pr.symbols.app (staging shell
// f6ce7c87e, Vault workspace 6a9d6f43208bdbbaed744900, 2026-09-10):
// the server row was set to `hidden_widgets: {}` — "nothing is hidden" — while
// the device's cache still held eight ids from an earlier session. The board
// booted with EIGHT widgets hidden. The row said the opposite and lost.
//
// The cause was one line of shape-checking: the saved set was read through
// `_nonEmptyMap`, which maps `{}` to `null`, and `null` means "no answer, fall
// through to the cache". So an EMPTY map — the perfectly ordinary state of a
// user who has hidden nothing, or who has just restored everything — was
// indistinguishable from "the row has not loaded yet". The consequences are
// exactly the two Nika reported:
//   • restore every widget on device A ⇒ the row empties ⇒ device B keeps
//     showing A's old hides forever, because B's cache outranks the row;
//   • clear a device's cache and the board changes — proof the device, not the
//     account, was holding the answer.
//
// PRESENCE, NOT EMPTINESS, is the test. A row that provably belongs to this
// workspace scope IS the answer, whatever it contains. The cache is consulted
// only when no such row exists yet.
//
// THE VERSION GATE keeps its meaning and gains a missing half. A row stamped
// below `currentVersion` is deliberately ignored once, so a default rollout can
// reach a member who has customised (`CURRENT_DASHBOARD_V` in pages/main.js).
// The cache MUST NOT stand in for that row: it is the same generation of the
// same answer, so honouring it would quietly defeat the reset for every user
// who still had one. A stale row therefore yields NO saved set at all.
//
// Pure by design: the caller supplies every input, so a test can pin the
// decision without a browser, a network or a storage API.

/**
 * @param {object} args
 * @param {object|null} args.row         the prefs row mirrored onto root state,
 *                                       or null when none has landed
 * @param {string|null} args.rowScope    the workspace the row tags itself with
 * @param {string} args.scope            the workspace scope being booted
 * @param {number} args.rowVersion       the row's `dashboard_v`
 * @param {number} args.currentVersion   `CURRENT_DASHBOARD_V`
 * @param {object|null} args.cache       the parsed localStorage cache, or null
 * @returns {{ saved: object|null, source: 'server'|'cache'|'none' }}
 *   `saved` is the map to compose over the base; `source` names who answered,
 *   which the caller uses to decide whether the cache still needs a refresh.
 */
export const resolveSavedPrefsSource = ({
  row,
  rowScope,
  scope,
  rowVersion,
  currentVersion,
  cache
} = {}) => {
  const scoped =
    row && typeof row === 'object' && !Array.isArray(row) && rowScope && rowScope === scope

  if (scoped) {
    // A row below the current schema version is the one-time reset-everyone.
    // It answers "ignore every saved set", and the cache is part of that set.
    if (Number(rowVersion || 0) < Number(currentVersion || 0)) {
      return { saved: null, source: 'server' }
    }
    // Present ⇒ authoritative, `{}` included. `{}` is a real answer: "nothing
    // is hidden here". This is the whole fix.
    return { saved: row, source: 'server' }
  }

  // No row for this scope yet — the GET is still in flight, the user has never
  // saved, or this boot has no network. The cache paints so the board is not
  // blank, and the server's answer replaces it the moment it lands.
  if (cache && typeof cache === 'object' && !Array.isArray(cache)) {
    return { saved: cache, source: 'cache' }
  }
  return { saved: null, source: 'none' }
}

export default { resolveSavedPrefsSource }
